import { useState, useRef } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  UploadSimple, File as FileIcon, X, CheckCircle, EnvelopeSimple, Phone, Clock, MapPin,
} from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useContent } from "@/context/ContentContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BUDGETS = [
  "Under $50k",
  "$50k - $150k",
  "$150k - $500k",
  "$500k - $1M",
  "$1M - $5M",
  "Over $5M",
  "Not sure yet",
];

const SOURCES = [
  "Google Search",
  "Referral",
  "Social Media",
  "Existing Client",
  "Industry Event",
  "Other",
];

const EMPTY = {
  full_name: "",
  email: "",
  phone: "",
  project_address: "",
  project_details: "",
  budget: "",
  hear_about_us: "",
};

export default function Contact() {
  const { content } = useContent();
  const c = content.contact || {};
  const [form, setForm] = useState(EMPTY);
  const [photos, setPhotos] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const photoRef = useRef(null);
  const docRef = useRef(null);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const addFiles = (setter) => (e) => {
    const incoming = Array.from(e.target.files || []);
    setter((prev) => [...prev, ...incoming]);
    e.target.value = "";
  };

  const removeFile = (setter, idx) => () =>
    setter((prev) => prev.filter((_, i) => i !== idx));

  const validate = () => {
    if (!form.full_name.trim()) return "Please enter your full name.";
    if (!form.email.trim() || !/^\S+@\S+\.\S+$/.test(form.email)) return "Please enter a valid email address.";
    if (!form.phone.trim()) return "Please enter your phone number.";
    if (!form.project_address.trim()) return "Please enter the project address / location.";
    if (!form.project_details.trim()) return "Please describe your project.";
    if (!form.budget) return "Please select a planned budget.";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      photos.forEach((f) => fd.append("photos", f));
      documents.forEach((f) => fd.append("documents", f));

      await axios.post(`${API}/consultations`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setDone(true);
      setForm(EMPTY);
      setPhotos([]);
      setDocuments([]);
      toast.success("Consultation request submitted!");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e2) {
      console.error(e2);
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  const FileList = ({ files, setter }) =>
    files.length > 0 && (
      <ul className="mt-3 space-y-2">
        {files.map((f, i) => (
          <li key={`${f.name}-${i}`} data-testid={`file-item-${i}`}
            className="flex items-center justify-between gap-3 bg-gray-50 border border-gray-200 px-3 py-2 rounded-sm">
            <span className="flex items-center gap-2 text-sm text-gray-700 truncate">
              <FileIcon size={16} className="text-gray-900 shrink-0" />
              <span className="truncate">{f.name}</span>
            </span>
            <button type="button" onClick={removeFile(setter, i)}
              className="text-gray-400 hover:text-red-500 transition-colors duration-200 shrink-0" aria-label="Remove file">
              <X size={16} />
            </button>
          </li>
        ))}
      </ul>
    );

  return (
    <div data-testid="contact-page">
      {/* Header */}
      <section className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-10 bg-gray-300" />
            <span className="text-xs font-bold uppercase tracking-[0.28em] text-gray-400">Get Started</span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-gray-900">
            Request a Project Consultation
          </h1>
          <p className="mt-5 text-sm text-gray-500 max-w-xl leading-relaxed">
            No consultation fee. We'll review your project and get back to you within 1-2 business days.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid gap-12 lg:grid-cols-12">
        {/* Form */}
        <div className="lg:col-span-7">
          {done && (
            <motion.div data-testid="success-banner" initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
              className="mb-8 flex items-start gap-3 border border-gray-300 bg-gray-100 p-5 rounded-sm">
              <CheckCircle size={24} weight="fill" className="text-gray-900 shrink-0 mt-0.5" />
              <div>
                <p className="font-heading font-semibold text-gray-900">Request received.</p>
                <p className="text-sm text-gray-600 mt-1">
                  Thank you. We will review your project and respond within 1-2 business days.
                </p>
              </div>
            </motion.div>
          )}

          <form onSubmit={handleSubmit} data-testid="consultation-form" className="space-y-6">
            <div className="grid gap-6 sm:grid-cols-2">
              <div>
                <Label htmlFor="full_name" className="text-sm font-medium text-gray-900">Full Name *</Label>
                <Input id="full_name" data-testid="input-full-name" value={form.full_name} onChange={update("full_name")}
                  placeholder="" className="mt-2 rounded-sm" />
              </div>
              <div>
                <Label htmlFor="email" className="text-sm font-medium text-gray-900">Email Address *</Label>
                <Input id="email" type="email" data-testid="input-email" value={form.email} onChange={update("email")}
                  placeholder="" className="mt-2 rounded-sm" />
              </div>
              <div>
                <Label htmlFor="phone" className="text-sm font-medium text-gray-900">Phone Number *</Label>
                <Input id="phone" data-testid="input-phone" value={form.phone} onChange={update("phone")}
                  placeholder="" className="mt-2 rounded-sm" />
              </div>
              <div>
                <Label htmlFor="project_address" className="text-sm font-medium text-gray-900">Project Address / Location *</Label>
                <Input id="project_address" data-testid="input-project-address" value={form.project_address} onChange={update("project_address")}
                  placeholder="" className="mt-2 rounded-sm" />
              </div>
            </div>

            <div>
              <Label htmlFor="project_details" className="text-sm font-medium text-gray-900">Project Details *</Label>
              <Textarea id="project_details" data-testid="input-project-details" value={form.project_details} onChange={update("project_details")}
                placeholder="" rows={6} className="mt-2 rounded-sm resize-none" />
            </div>

            <div className="grid gap-6 sm:grid-cols-2">
              <div>
                <Label className="text-sm font-medium text-gray-900">Planned Project Budget *</Label>
                <Select value={form.budget} onValueChange={(v) => setForm((f) => ({ ...f, budget: v }))}>
                  <SelectTrigger data-testid="select-budget" className="mt-2 rounded-sm">
                    <SelectValue placeholder="Select a budget range" />
                  </SelectTrigger>
                  <SelectContent>
                    {BUDGETS.map((b) => (
                      <SelectItem key={b} value={b} data-testid={`budget-option-${b}`}>{b}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-medium text-gray-900">How did you hear about us?</Label>
                <Select value={form.hear_about_us} onValueChange={(v) => setForm((f) => ({ ...f, hear_about_us: v }))}>
                  <SelectTrigger data-testid="select-source" className="mt-2 rounded-sm">
                    <SelectValue placeholder="Select an option" />
                  </SelectTrigger>
                  <SelectContent>
                    {SOURCES.map((s) => (
                      <SelectItem key={s} value={s} data-testid={`source-option-${s}`}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid gap-6 sm:grid-cols-2">
              <div>
                <Label className="text-sm font-medium text-gray-900">Upload Photos</Label>
                <button type="button" data-testid="upload-photos-button" onClick={() => photoRef.current?.click()}
                  className="mt-2 w-full border border-dashed border-gray-300 rounded-sm px-4 py-6 flex flex-col items-center gap-2 text-gray-500 hover:border-gray-900 hover:text-gray-900 transition-colors duration-200">
                  <UploadSimple size={24} />
                  <span className="text-sm">Click to add photos</span>
                  <span className="text-xs text-gray-400">Multiple files supported</span>
                </button>
                <input ref={photoRef} type="file" accept="image/*" multiple hidden data-testid="photos-input" onChange={addFiles(setPhotos)} />
                <FileList files={photos} setter={setPhotos} />
              </div>
              <div>
                <Label className="text-sm font-medium text-gray-900">Upload Documents</Label>
                <button type="button" data-testid="upload-documents-button" onClick={() => docRef.current?.click()}
                  className="mt-2 w-full border border-dashed border-gray-300 rounded-sm px-4 py-6 flex flex-col items-center gap-2 text-gray-500 hover:border-gray-900 hover:text-gray-900 transition-colors duration-200">
                  <UploadSimple size={24} />
                  <span className="text-sm">Click to add documents</span>
                  <span className="text-xs text-gray-400">PDF, DOC, plans, etc.</span>
                </button>
                <input ref={docRef} type="file" multiple hidden data-testid="documents-input" onChange={addFiles(setDocuments)} />
                <FileList files={documents} setter={setDocuments} />
              </div>
            </div>

            <button type="submit" data-testid="submit-consultation-button" disabled={submitting}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-gray-900 text-white font-semibold px-8 py-4 rounded-sm hover:bg-gray-700 transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed">
              {submitting ? "Submitting..." : "Submit Consultation Request"}
            </button>

            <p data-testid="form-note" className="text-sm text-gray-500">
              We will review your request and respond within 1-2 business days.
            </p>
          </form>
        </div>

        {/* Sidebar */}
        <aside className="lg:col-span-5">
          <div className="bg-gray-950 text-white p-8 rounded-sm">
            <h2 className="font-heading text-xl font-semibold">Talk to our team</h2>
            <p className="mt-3 text-sm text-gray-400 leading-relaxed">
              Prefer to reach out directly? We are happy to discuss your project, timeline, and budget.
            </p>
            <ul className="mt-8 space-y-5 text-sm">
              <li className="flex items-start gap-3">
                <EnvelopeSimple size={20} className="text-gray-500 mt-0.5" />
                <div>
                  <p className="text-gray-400">Email</p>
                  <p className="text-white">{c.email}</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Phone size={20} className="text-gray-500 mt-0.5" />
                <div>
                  <p className="text-gray-400">Phone</p>
                  <p className="text-white">{c.phone}</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <MapPin size={20} className="text-gray-500 mt-0.5" />
                <div>
                  <p className="text-gray-400">Coverage</p>
                  <p className="text-white">{c.coverage}</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Clock size={20} className="text-gray-500 mt-0.5" />
                <div>
                  <p className="text-gray-400">Response Time</p>
                  <p className="text-white">{c.response_time}</p>
                </div>
              </li>
            </ul>
          </div>
        </aside>
      </section>
    </div>
  );
}
